package com.example.hukakmp.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.example.hukakmp.chat.ChatPersistence
import com.example.hukakmp.chat.HukaChatMessage
import com.example.hukakmp.chat.HukaChatResult
import com.example.hukakmp.chat.askHuka
import com.example.hukakmp.data.AiQuotaRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json

class HukaChatViewModel(
    private val uid: String,
    private val quotaRepository: AiQuotaRepository,
) : ViewModel() {

    data class UiState(
        val messages: List<HukaChatMessage> = emptyList(),
        val isTyping: Boolean = false,
        val error: String? = null,
        val chatsRemaining: Int = 5,
        val chatsTotal: Int = 5,
    )

    private val json = Json { ignoreUnknownKeys = true }
    private val greeting = HukaChatMessage(
        text = "Hola, soy Huka. Preguntame lo que necesites sobre pesca.",
        fromUser = false,
    )

    private val _uiState = MutableStateFlow(
        UiState(messages = loadHistory().ifEmpty { listOf(greeting) }),
    )
    val uiState: StateFlow<UiState> = _uiState.asStateFlow()

    init {
        persistMessages(_uiState.value.messages)
        refreshQuota()
    }

    fun refreshQuota() {
        viewModelScope.launch {
            val quota = quotaRepository.getState()
            _uiState.value = _uiState.value.copy(
                chatsRemaining = quota.chatsRemaining,
                chatsTotal = quota.chatsTotal,
            )
        }
    }

    fun send(message: String) {
        val clean = message.trim()
        if (clean.isBlank() || _uiState.value.isTyping) return

        viewModelScope.launch {
            val quota = quotaRepository.getState()
            if (quota.chatsRemaining <= 0) {
                _uiState.value = _uiState.value.copy(
                    chatsRemaining = 0,
                    chatsTotal = quota.chatsTotal,
                    error = "Alcanzaste el límite diario de consultas de Huka. Se renueva al comenzar un nuevo día.",
                )
                return@launch
            }

            val historyBefore = _uiState.value.messages
            val withUserMessage = historyBefore + HukaChatMessage(clean, fromUser = true)
            _uiState.value = _uiState.value.copy(
                messages = withUserMessage,
                isTyping = true,
                error = null,
                chatsRemaining = quota.chatsRemaining,
                chatsTotal = quota.chatsTotal,
            )
            persistMessages(withUserMessage)

            when (val result = askHuka(clean, historyBefore)) {
                is HukaChatResult.Success -> {
                    val consumed = quotaRepository.consumeChat()
                    val finalMessages = _uiState.value.messages +
                        HukaChatMessage(result.text, fromUser = false)

                    _uiState.value = _uiState.value.copy(
                        messages = finalMessages,
                        isTyping = false,
                        error = null,
                        chatsRemaining = consumed?.chatsRemaining
                            ?: _uiState.value.chatsRemaining,
                        chatsTotal = consumed?.chatsTotal
                            ?: _uiState.value.chatsTotal,
                    )
                    persistMessages(finalMessages)
                }

                is HukaChatResult.Error -> {
                    _uiState.value = _uiState.value.copy(
                        isTyping = false,
                        error = result.message,
                    )
                }
            }
        }
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    fun resetConversation() {
        ChatPersistence.clear(uid)
        _uiState.value = _uiState.value.copy(
            messages = listOf(greeting),
            error = null,
        )
        persistMessages(_uiState.value.messages)
    }

    private fun loadHistory(): List<HukaChatMessage> {
        val raw = ChatPersistence.load(uid) ?: return emptyList()
        return runCatching {
            json.decodeFromString(
                ListSerializer(HukaChatMessage.serializer()),
                raw,
            )
        }.getOrDefault(emptyList())
    }

    private fun persistMessages(messages: List<HukaChatMessage>) {
        runCatching {
            ChatPersistence.save(
                uid,
                json.encodeToString(
                    ListSerializer(HukaChatMessage.serializer()),
                    messages.takeLast(40),
                ),
            )
        }
    }
}
